import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, Link } from "react-router-dom";

const EditVendorTransaction = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const vendor = location.state?.vendor;
  const transaction = location.state?.transaction;
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    transaction_id: "",
    vendor_id: "",
    amount: "",
    transaction_type: "2", // 1 = IN, 2 = OUT
    transaction_date: new Date().toISOString().split("T")[0],
    remark: "",
    available_balance: 0,
  });

  const [currentOutstanding, setCurrentOutstanding] = useState(0);
  const [nextTransactions, setNextTransactions] = useState([]);

  // Use refs to track component mounting
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    if (vendor && transaction) {
      // Load vendor's current outstanding
      setCurrentOutstanding(parseFloat(vendor.vendor_outstanding || 0));

      // Set form data from transaction
      setFormData({
        transaction_id: transaction.transaction_id,
        vendor_id: transaction.vendor_id,
        amount: transaction.amount,
        transaction_type: transaction.transaction_type || "2",
        transaction_date: transaction.transaction_date,
        remark: transaction.remark || "",
        available_balance: parseFloat(transaction.available_balance),
      });

      // Load subsequent transactions for preview
      loadNextTransactions();
    }

    return () => {
      isMounted.current = false;
    };
  }, [vendor, transaction]);

  const loadNextTransactions = async () => {
    if (!vendor || !transaction || !isMounted.current) return;

    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vendor_id", vendor.vendor_id);
      formData.append("action", "view_transactions");
      formData.append("transaction_grage_id", grage_id);
      formData.append("from_date", transaction.transaction_date);
      formData.append("to_date", new Date().toISOString().split("T")[0]);

      const response = await axios.post(
        `${server_url}transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (
        isMounted.current &&
        response.data.status == "1" &&
        response.data.result
      ) {
        // Filter to get transactions after the current one
        const allTransactions = response.data.result;
        const currentIndex = allTransactions.findIndex(
          (t) => t.transaction_id === transaction.transaction_id
        );

        if (currentIndex !== -1) {
          // Get transactions after this one
          const next = allTransactions.slice(0, currentIndex); // Since it's sorted DESC, index 0 is most recent
          setNextTransactions(next);
        }
      }
    } catch (error) {
      console.error("Error loading next transactions:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedForm = {
        ...prev,
        [name]: value,
      };

      // If amount or type changed, calculate new available balance
      if (name === "amount" || name === "transaction_type") {
        // Get previous transaction's balance
        const previousTransactions = nextTransactions.filter(
          (t) => t.transaction_id !== formData.transaction_id
        );

        let runningBalance = currentOutstanding;

        // We need to reverse calculate to get the starting balance
        // This is simplified - in real app you might want to fetch previous balance from server
        // For now, we'll use the transaction's current available balance as reference
        const newBalance = calculateNewBalance(
          parseFloat(updatedForm.amount || 0),
          updatedForm.transaction_type,
          parseFloat(transaction.available_balance || 0)
        );

        updatedForm.available_balance = newBalance;
      }

      return updatedForm;
    });
  };

  const calculateNewBalance = (
    newAmount,
    newType,
    currentTransactionBalance
  ) => {
    // This calculates what the new balance would be for this transaction
    // Based on current transaction's previous balance and the new amount/type
    const oldAmount = parseFloat(transaction.amount || 0);
    const oldType = transaction.transaction_type;

    // Start with current transaction's balance
    let newBalance = currentTransactionBalance;

    // Remove old transaction effect
    if (oldType === "1") {
      // Old was IN
      newBalance -= oldAmount;
    } else {
      // Old was OUT
      newBalance += oldAmount;
    }

    // Add new transaction effect
    if (newType === "1") {
      // New is IN
      newBalance += newAmount;
    } else {
      // New is OUT
      newBalance -= newAmount;
    }

    return newBalance;
  };

  const calculateImpact = () => {
    const oldAmount = parseFloat(transaction.amount || 0);
    const oldType = transaction.transaction_type;
    const newAmount = parseFloat(formData.amount || 0);
    const newType = formData.transaction_type;

    let oldEffect = 0;
    let newEffect = 0;

    if (oldType === "1") {
      oldEffect = oldAmount; // IN adds to balance
    } else {
      oldEffect = -oldAmount; // OUT subtracts from balance
    }

    if (newType === "1") {
      newEffect = newAmount;
    } else {
      newEffect = -newAmount;
    }

    return newEffect - oldEffect; // Positive = net increase, Negative = net decrease
  };

  const openConfirmPopup = () => {
    if (!formData.amount || !formData.transaction_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setShowConfirmPopup(true);
  };

  const closeConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.transaction_date || !isMounted.current) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const postData = new FormData();
      postData.append("company_id", company_id);
      postData.append("transaction_id", formData.transaction_id);
      postData.append("vendor_id", formData.vendor_id);
      postData.append("amount", formData.amount);
      postData.append("remark", formData.remark);
      postData.append("transaction_date", formData.transaction_date);
      postData.append("transaction_type", formData.transaction_type);
      postData.append("available_balance", formData.available_balance);
      postData.append("transaction_grage_id", grage_id);
      postData.append("action", "update_transaction_with_rebalance");

      const response = await axios.post(
        `${server_url}transaction.php`,
        postData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (isMounted.current && response.data.status == "1") {
        toast.success("Transaction updated successfully!");
        navigate(-1); // Go back to transactions page
      } else if (isMounted.current) {
        toast.error(response.data.error || "Failed to update transaction");
      }
    } catch (error) {
      console.error("Error updating transaction:", error);
      if (isMounted.current) {
        toast.error("Unable to update transaction");
      }
    } finally {
      if (isMounted.current) {
        setSubmitting(false);
        setShowConfirmPopup(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!vendor || !transaction) {
    return (
      <div
        className="container"
        style={{ textAlign: "center", padding: "40px" }}
      >
        <h3>No transaction selected</h3>
        <p>Please go back and select a transaction to edit.</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: "#0352a1",
            color: "white",
            border: "none",
            padding: "10px 20px",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            marginTop: "15px",
          }}
        >
          <i className="fas fa-arrow-left"></i> Go Back
        </button>
      </div>
    );
  }

  const impact = calculateImpact();

  return (
    <div
      className="container"
      style={{ maxWidth: "800px", margin: "0 auto", padding: "10px" }}
    >
      {/* Header */}

      {/* Current Status */}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "15px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div>
            <div style={{ fontSize: "14px", color: "#4a5568" }}>
              Current Outstanding
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color:
                  currentOutstanding > 0
                    ? "#dc2626"
                    : currentOutstanding < 0
                    ? "#f59e0b"
                    : "#10b981",
              }}
            >
              {formatCurrency(currentOutstanding)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "14px", color: "#4a5568" }}>
              Impact on Balance
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "bold",
                color:
                  impact > 0 ? "#10b981" : impact < 0 ? "#dc2626" : "#6b7280",
              }}
            >
              {impact > 0 ? "+" : ""}
              {formatCurrency(impact)}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <div
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "10px",
          marginBottom: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <h3 style={{ margin: "0 0 20px 0", color: "#444" }}>
          Transaction Details
        </h3>

        <div
          style={{
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              Amount *
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              required
              step="0.01"
              min="0.01"
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              Transaction Type *
            </label>
            <select
              name="transaction_type"
              value={formData.transaction_type}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
                backgroundColor: "white",
              }}
            >
              <option value="2">OUT (Payment to Vendor)</option>
              <option value="1">IN (Payment from Vendor)</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              Date *
            </label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "600",
                color: "#555",
                fontSize: "14px",
              }}
            >
              New Balance After This Transaction
            </label>
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f8f9fa",
                border: "2px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "16px",
                textAlign: "center",
                fontWeight: "bold",
                color:
                  formData.available_balance > 0
                    ? "#dc2626"
                    : formData.available_balance < 0
                    ? "#f59e0b"
                    : "#10b981",
              }}
            >
              {formatCurrency(formData.available_balance)}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "600",
              color: "#555",
              fontSize: "14px",
            }}
          >
            Remarks
          </label>
          <textarea
            name="remark"
            value={formData.remark}
            onChange={handleInputChange}
            rows="3"
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: "14px",
              resize: "vertical",
            }}
            placeholder="Enter any remarks..."
          />
        </div>

      
        <button
          onClick={openConfirmPopup}
          disabled={submitting || !formData.amount}
          style={{
            backgroundColor: "#f59e0b",
            color: "white",
            padding: "12px 30px",
            width: "100%",
            border: "none",
            borderRadius: "8px",
            fontSize: "16px",
            cursor: submitting || !formData.amount ? "not-allowed" : "pointer",
            opacity: submitting || !formData.amount ? 0.7 : 1,
            fontWeight: "600",
          }}
        >
          {submitting ? (
            <>
              <i
                className="fas fa-spinner fa-spin"
                style={{ marginRight: "8px" }}
              ></i>
              Updating...
            </>
          ) : (
            <>
              <i className="fas fa-save" style={{ marginRight: "8px" }}></i>
              Update Transaction
            </>
          )}
        </button>
      </div>

      {/* Preview of Subsequent Transactions */}
      {/* {nextTransactions.length > 0 && (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "10px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
         
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#f5f5f5" }}>
                  <th
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      borderBottom: "2px solid #ddd",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    Date
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      borderBottom: "2px solid #ddd",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      borderBottom: "2px solid #ddd",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      borderBottom: "2px solid #ddd",
                      color: "#555",
                      fontSize: "14px",
                    }}
                  >
                    Current Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {nextTransactions.map((t, index) => (
                  <tr
                    key={t.transaction_id}
                    style={{
                      borderBottom: "1px solid #eee",
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <td style={{ padding: "10px", fontSize: "14px" }}>
                      {formatDate(t.transaction_date)}
                    </td>
                    <td style={{ padding: "10px", fontSize: "14px" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: "600",
                          backgroundColor:
                            t.transaction_type === "1" ? "#d4edda" : "#f8d7da",
                          color:
                            t.transaction_type === "1" ? "#155724" : "#721c24",
                        }}
                      >
                        {t.transaction_type === "1" ? "IN" : "OUT"}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "10px",
                        fontWeight: "600",
                        fontSize: "14px",
                      }}
                    >
                      {formatCurrency(parseFloat(t.amount))}
                    </td>
                    <td style={{ padding: "10px", fontSize: "14px" }}>
                      <span
                        style={{
                          fontWeight: "600",
                          color:
                            parseFloat(t.available_balance) > 0
                              ? "#dc2626"
                              : parseFloat(t.available_balance) < 0
                              ? "#f59e0b"
                              : "#10b981",
                        }}
                      >
                        {formatCurrency(parseFloat(t.available_balance))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )} */}

      {/* Confirmation Popup */}
      {showConfirmPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "450px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fef3c7",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "28px", color: "#f59e0b" }}
              ></i>
            </div>
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Update Transaction
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                color: "#6b7280",
                fontSize: "14px",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to update this transaction? This will
              recalculate all subsequent transaction balances.
            </p>

            <div
              style={{
                backgroundColor: "#f8f9fa",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
                textAlign: "left",
              }}
            >
              <div style={{ marginBottom: "10px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>
                  Amount:{" "}
                </span>
                <span style={{ color: "#1f2937" }}>
                  {formatCurrency(parseFloat(formData.amount))}
                </span>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>Type: </span>
                <span
                  style={{
                    color:
                      formData.transaction_type === "1" ? "#155724" : "#721c24",
                    backgroundColor:
                      formData.transaction_type === "1" ? "#d4edda" : "#f8d7da",
                    padding: "3px 10px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {formData.transaction_type === "1" ? "IN" : "OUT"}
                </span>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>Date: </span>
                <span style={{ color: "#1f2937" }}>
                  {formatDate(formData.transaction_date)}
                </span>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <span style={{ fontWeight: "600", color: "#555" }}>
                  New Balance:{" "}
                </span>
                <span
                  style={{
                    color:
                      formData.available_balance > 0
                        ? "#dc2626"
                        : formData.available_balance < 0
                        ? "#f59e0b"
                        : "#10b981",
                    fontWeight: "bold",
                  }}
                >
                  {formatCurrency(formData.available_balance)}
                </span>
              </div>
              {impact !== 0 && (
                <div>
                  <span style={{ fontWeight: "600", color: "#555" }}>
                    Net Change:{" "}
                  </span>
                  <span
                    style={{
                      color: impact > 0 ? "#10b981" : "#dc2626",
                      fontWeight: "bold",
                    }}
                  >
                    {impact > 0 ? "+" : ""}
                    {formatCurrency(impact)}
                  </span>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeConfirmPopup}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#f59e0b",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {submitting ? (
                  <>
                    <i
                      className="fas fa-spinner fa-spin"
                      style={{ marginRight: "5px" }}
                    ></i>
                    Updating...
                  </>
                ) : (
                  "Confirm Update"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 10px 5px;
          }
          div[style*="gridTemplateColumns"] {
            grid-template-columns: 1fr;
          }
          table {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default EditVendorTransaction;
